import type { LocalDate, LocalDateTime, LocalTime } from '../util/datetime';
import { dateTimeOf, isAfter, isBefore, plusHours } from '../util/datetime';
import type { Address } from './restaurant';

export type ReservationStatus = 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';

export interface GuestDetails {
  readonly fullName: string;
  readonly phone: string;
  readonly email: string;
}

export interface CancellationPolicy {
  readonly freeCancellationHours: number;
  readonly holdMinutes: number;
}

export const DEFAULT_CANCELLATION_POLICY: CancellationPolicy = {
  freeCancellationHours: 2,
  holdMinutes: 15,
};

export interface Reservation {
  readonly id: string;
  /** Código legible que el comensal enseña en el restaurante. Nunca vacío. */
  readonly confirmationCode: string;
  readonly restaurantId: string;
  readonly restaurantName: string;
  readonly restaurantPhoto: string | null;
  readonly address: Address | null;
  readonly date: LocalDate;
  readonly time: LocalTime;
  readonly partySize: number;
  readonly status: ReservationStatus;
  readonly guest: GuestDetails;
  readonly occasion: string | null;
  readonly notes: string | null;
  readonly createdAt: LocalDateTime | null;
  readonly cancellationPolicy: CancellationPolicy;
}

/**
 * Resultado de preguntar si una reserva se puede cancelar.
 *
 * No es un booleano a propósito: cancelar existe como acción en toda reserva futura, pero puede
 * quedar fuera de la ventana de la política. Distinguir los tres casos permite que el botón
 * **no se renderice** cuando no aplica y se muestre **deshabilitado con explicación** cuando la
 * política lo bloquea, en lugar de desaparecer sin que el usuario entienda por qué.
 */
export type Cancellability =
  | { readonly kind: 'NOT_APPLICABLE' }
  | { readonly kind: 'BLOCKED_BY_POLICY'; readonly hours: number }
  | { readonly kind: 'ALLOWED' };

export function reservationDateTime(reservation: Reservation): LocalDateTime {
  return dateTimeOf(reservation.date, reservation.time);
}

export function isUpcoming(reservation: Reservation, now: LocalDateTime): boolean {
  return reservation.status === 'CONFIRMED' && isAfter(reservationDateTime(reservation), now);
}

export function isPast(reservation: Reservation, now: LocalDateTime): boolean {
  return reservation.status !== 'CANCELLED' && !isAfter(reservationDateTime(reservation), now);
}

/**
 * Modificar solo tiene sentido en una reserva futura y confirmada.
 * Si devuelve false, el botón **no se renderiza** (no existe la acción).
 */
export function canModify(reservation: Reservation, now: LocalDateTime): boolean {
  return isUpcoming(reservation, now);
}

export function cancellability(reservation: Reservation, now: LocalDateTime): Cancellability {
  const when = reservationDateTime(reservation);
  if (reservation.status === 'CANCELLED') return { kind: 'NOT_APPLICABLE' };
  if (!isAfter(when, now)) return { kind: 'NOT_APPLICABLE' };
  const deadline = plusHours(when, -reservation.cancellationPolicy.freeCancellationHours);
  if (isBefore(deadline, now)) {
    return { kind: 'BLOCKED_BY_POLICY', hours: reservation.cancellationPolicy.freeCancellationHours };
  }
  return { kind: 'ALLOWED' };
}
